import React, { useState } from "react";
import {
  Anchor,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { SettingsField } from "../common";
import StudioTabShell from "./StudioTabShell";
import { useFeatureAccess } from "../../../context/FeatureAccessContext";
import { isDegradedSalonRecord } from "../../../utils/currentLocation";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import { US_STATES, US_TIMEZONES } from "../../../utils/formConstants";
import {
  BUSINESS_VERTICAL_OPTIONS,
  STAFFING_MODEL_OPTIONS,
  BusinessInfoValues,
  buildBusinessInfoPayload,
  formatBusinessVertical,
  formatPhoneForDisplay,
  formatStaffingModel,
  formatTimezone,
  normalizeBusinessInfo,
  validateBusinessInfo,
} from "../../../utils/studioSettings";

const VERTICAL_LOCKED_HELP =
  "This is locked while your subscription is active. Contact support to change it — we'll migrate your subscription and settings for you.";

export interface BusinessInformationTabProps {
  businessInfo: any;
  editing: boolean;
  saving?: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  /** Persist a partial salon payload; returns when the save settles. */
  onSave: (payload: Record<string, any>) => void | Promise<void>;
}

export default function BusinessInformationTab({
  businessInfo,
  editing,
  saving = false,
  readOnly = false,
  onEdit,
  onCancel,
  onSave,
}: BusinessInformationTabProps) {
  const { access } = useFeatureAccess();
  const [values, setValues] = useState<BusinessInfoValues>(() =>
    normalizeBusinessInfo(businessInfo),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Type / staffing model are locked once the salon is on a paid subscription
  // (changes route through support so the Stripe subscription is migrated too).
  const verticalLocked =
    !!access?.subscription_tier && access.subscription_tier !== "trial";
  // A degraded /access-derived record is missing real fields; saving would
  // overwrite the DB with blanks, so block it (mirrors BusinessInfoForm).
  const isDegraded = isDegradedSalonRecord(businessInfo);

  // When Business Type / Staffing Model are locked, surface the "why" on hover
  // of the disabled field rather than as always-visible description text. The
  // tooltip listens on the Select's (enabled) root, so hover still fires over
  // the disabled input.
  const withLockedTooltip = (control: React.ReactNode) =>
    verticalLocked ? (
      <Tooltip
        label={VERTICAL_LOCKED_HELP}
        multiline
        w={280}
        withArrow
        position="top-start"
        events={{ hover: true, focus: true, touch: true }}
      >
        {control}
      </Tooltip>
    ) : (
      control
    );

  const startEdit = () => {
    setValues(normalizeBusinessInfo(businessInfo));
    setErrors({});
    onEdit();
  };

  const setField = (field: keyof BusinessInfoValues, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  };

  const handleSave = () => {
    if (isDegraded) return;
    const validationErrors = validateBusinessInfo(values);
    // Business type + staffing model are required, but only enforce them while
    // they're editable: a paid salon's taxonomy is locked here and the backend
    // rejects an empty business_vertical, so we neither require nor resend it.
    if (!verticalLocked) {
      if (!values.business_vertical) {
        validationErrors.business_vertical = "Business type is required";
      }
      if (!values.staffing_model) {
        validationErrors.staffing_model = "Staffing model is required";
      }
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    // Include the salon id (PUT /business-locations keys the update off it) and only this
    // view's fields. Drop the locked taxonomy so we never resend an
    // unchangeable business_vertical/staffing_model.
    const payload: Record<string, any> = {
      id: businessInfo?.id,
      ...buildBusinessInfoPayload(values),
    };
    if (verticalLocked) {
      delete payload.business_vertical;
      delete payload.staffing_model;
    }
    onSave(payload);
  };

  const phoneDisplay = formatPhoneForDisplay(
    businessInfo?.area_code,
    businessInfo?.phone_number,
  );
  const showStaffingModel = Boolean(
    editing ? values.business_vertical : businessInfo?.business_vertical,
  );

  return (
    <StudioTabShell
      editing={editing}
      readOnly={readOnly}
      saving={saving}
      onEdit={startEdit}
      onCancel={onCancel}
      onSave={handleSave}
      saveDisabled={isDegraded}
      saveDisabledReason="Some of this location's settings haven't finished loading, so saving now would overwrite them with blank values. Please refresh the page and try again."
    >
      <SettingsField
        label="Business Name"
        editing={editing}
        required={editing}
        editControl={
          <TextInput
            aria-label="Business Name"
            required
            value={values.salon_name}
            onChange={(e) => setField("salon_name", e.target.value)}
            error={errors.salon_name}
            w="100%"
          />
        }
      >
        {businessInfo?.salon_name || "N/A"}
      </SettingsField>

      <SettingsField
        label="Legal Business Name"
        editing={editing}
        editControl={
          <TextInput
            aria-label="Legal Business Name"
            value={values.legal_business_name}
            onChange={(e) => setField("legal_business_name", e.target.value)}
            w="100%"
          />
        }
      >
        {businessInfo?.legal_business_name || "N/A"}
      </SettingsField>

      <SettingsField
        label="Business Type"
        editing={editing}
        required={editing}
        editControl={withLockedTooltip(
          <Select
            aria-label="Business Type"
            required
            data={BUSINESS_VERTICAL_OPTIONS}
            value={values.business_vertical || null}
            onChange={(v) => setField("business_vertical", v || "")}
            clearable={!verticalLocked}
            disabled={verticalLocked}
            error={errors.business_vertical}
            w="100%"
          />,
        )}
      >
        {formatBusinessVertical(businessInfo?.business_vertical)}
      </SettingsField>

      {showStaffingModel && (
        <SettingsField
          label="Staffing Model"
          editing={editing}
          required={editing}
          editControl={withLockedTooltip(
            <Select
              aria-label="Staffing Model"
              required
              data={STAFFING_MODEL_OPTIONS}
              value={values.staffing_model || null}
              onChange={(v) => setField("staffing_model", v || "")}
              clearable={!verticalLocked}
              disabled={verticalLocked}
              error={errors.staffing_model}
              w="100%"
            />,
          )}
        >
          {formatStaffingModel(businessInfo?.staffing_model)}
        </SettingsField>
      )}

      <SettingsField
        label="Phone"
        editing={editing}
        required={editing}
        editControl={
          <TextInput
            aria-label="Phone"
            required
            placeholder="(xxx) xxx-xxxx"
            value={values.phone}
            onChange={(e) =>
              setField("phone", formatPhoneNumber(e.target.value))
            }
            error={errors.phone}
            w="100%"
          />
        }
      >
        {phoneDisplay || "N/A"}
      </SettingsField>

      <SettingsField
        label="Address"
        editing={editing}
        required={editing}
        editControl={
          <Stack gap="xs" w="100%">
            <TextInput
              aria-label="Street Address Line 1"
              required
              placeholder="Street address"
              value={values.streetAddress1}
              onChange={(e) => setField("streetAddress1", e.target.value)}
              error={errors.streetAddress1}
            />
            <TextInput
              aria-label="Street Address Line 2"
              placeholder="Apt, suite, etc. (optional)"
              value={values.streetAddress2}
              onChange={(e) => setField("streetAddress2", e.target.value)}
            />
            <TextInput
              aria-label="City"
              required
              placeholder="City"
              value={values.city}
              onChange={(e) => setField("city", e.target.value)}
              error={errors.city}
            />
            <Grid gutter="xs">
              <Grid.Col span={6}>
                <Select
                  aria-label="State"
                  required
                  placeholder="State"
                  data={US_STATES}
                  value={values.state || null}
                  onChange={(v) => setField("state", v || "")}
                  error={errors.state}
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  aria-label="Zip Code"
                  required
                  placeholder="Zip"
                  value={values.zip_code}
                  onChange={(e) => setField("zip_code", e.target.value)}
                  error={errors.zip_code}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        }
      >
        <Stack gap={0}>
          <Text inherit>{businessInfo?.street_address_1 || "N/A"}</Text>
          {businessInfo?.street_address_2 && (
            <Text inherit>{businessInfo.street_address_2}</Text>
          )}
          {(businessInfo?.city ||
            businessInfo?.state ||
            businessInfo?.zip_code) && (
            <Text inherit>
              {businessInfo?.city}, {businessInfo?.state}{" "}
              {businessInfo?.zip_code}
            </Text>
          )}
        </Stack>
      </SettingsField>

      <SettingsField
        label="Time Zone"
        editing={editing}
        required={editing}
        editControl={
          <Select
            aria-label="Time Zone"
            required
            data={US_TIMEZONES}
            value={values.timezone || null}
            onChange={(v) => setField("timezone", v || "")}
            error={errors.timezone}
            w="100%"
          />
        }
      >
        {formatTimezone(businessInfo?.timezone)}
      </SettingsField>

      <SettingsField
        label="Google Review Link"
        info="Navigate to your Google Business Profile and click 'Ask for reviews' to get your link."
        editing={editing}
        withDivider={false}
        editControl={
          <TextInput
            aria-label="Google Review Link"
            placeholder="https://g.page/r/..."
            value={values.google_review_link}
            onChange={(e) => setField("google_review_link", e.target.value)}
            error={errors.google_review_link}
            w="100%"
          />
        }
      >
        {businessInfo?.google_review_link ? (
          <Anchor
            href={businessInfo.google_review_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Group gap={4} wrap="nowrap" justify="flex-end">
              {businessInfo.google_review_link}
              <IconExternalLink size={14} />
            </Group>
          </Anchor>
        ) : (
          "N/A"
        )}
      </SettingsField>
    </StudioTabShell>
  );
}
